import React, { useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// InboundHolidays.tsx
//
// Single service detail page — same visual identity as the other
// pages on the site: teal / gold / cream palette, Playfair Display
// + DM Sans, the dune-silhouette divider, and the same button and
// section conventions. Swap the `hero`/`split` image URLs below
// for your own photography whenever it's ready.
// ============================================================

type IconName = 'check' | 'clock' | 'shield' | 'user' | 'doc' | 'map' | 'star' | 'phone' | 'globe' | 'route';

interface Highlight {
  icon: IconName;
  title: string;
  description: string;
}

interface Step {
  n: string;
  title: string;
  description: string;
}

interface Faq {
  q: string;
  a: string;
}

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1699855803432-17f4ab468b2d?w=1800&q=80&fit=crop&crop=center',
  split: 'https://images.unsplash.com/photo-1699855803432-17f4ab468b2d?w=1200&q=80&fit=crop&crop=center',
};

const highlights: Highlight[] = [
  { icon: 'map', title: "Custom Itineraries", description: "Leisure, business, or group travel planned around your actual goals." },
  { icon: 'user', title: "Licensed Local Guides", description: "Guides who know the sites, the history, and the practical details." },
  { icon: 'route', title: "Private or Shared Transport", description: "Vehicles matched to your group size, from sedans to full coaches." },
  { icon: 'shield', title: "MICE-Ready Logistics", description: "Venues, catering, and delegate travel coordinated as one package." }
];

const steps: Step[] = [
  { n: '01', title: "Tell Us Who\'s Coming & Why", description: "Group size, purpose of travel, and any dates already fixed." },
  { n: '02', title: "Itinerary Drafted Around Your Goals", description: "A day-by-day plan built for leisure, business, or a mix of both." },
  { n: '03', title: "Ground Logistics Confirmed", description: "Transport, guides, and venues booked and confirmed in writing." },
  { n: '04', title: "On-the-Ground Support", description: "A local contact reachable throughout the visit, not just before it." }
];

const faqs: Faq[] = [
  { q: "Is there a minimum group size?", a: "No — we arrange trips for solo travelers, families, and groups of fifty or more, with the itinerary adjusted accordingly." },
  { q: "Can the itinerary be customised after booking?", a: "Within reason, yes. Let us know what\'s changed and we\'ll adjust transport, guides, and bookings where possible." },
  { q: "How much notice do you need for group trips?", a: "For larger groups or MICE events, two to three weeks lets us secure the best venues and vehicles; smaller trips can often be arranged faster." }
];

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

function Icon({ name }: { name: IconName }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'check':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>;
    case 'clock':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.2 3.2" /></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /></svg>;
    case 'user':
      return <svg {...common}><circle cx="12" cy="8" r="3" /><path d="M5 21c0-4 3-6.5 7-6.5S19 17 19 21" /></svg>;
    case 'doc':
      return <svg {...common}><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M9 8h6M9 12h6M9 16h4" /></svg>;
    case 'map':
      return <svg {...common}><path d="M9 20l-5-2V6l5 2 6-2 5 2v12l-5-2-6 2z" /><path d="M9 6v14M15 4v14" /></svg>;
    case 'star':
      return <svg {...common}><path d="M12 3l2.6 5.7 6.2.6-4.7 4.2 1.4 6.1L12 16.9 6.5 19.6l1.4-6.1L3.2 9.3l6.2-.6L12 3z" /></svg>;
    case 'phone':
      return <svg {...common}><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>;
    case 'globe':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15.5 0 18M12 3c-2.5 2.5-2.5 15.5 0 18" /></svg>;
    case 'route':
      return <svg {...common}><circle cx="6" cy="6" r="2.3" /><circle cx="18" cy="18" r="2.3" /><path d="M6 8.3V13a3 3 0 0 0 3 3h6" /></svg>;
    default:
      return null;
  }
}

export default function InboundHolidays() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }

        /* HERO */
        .svc-hero { position: relative; min-height: 480px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .svc-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.32; }
        .svc-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .svc-hero-inner { position: relative; z-index: 2; padding: 64px 0 40px; }
        .svc-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 660px; margin-bottom: 16px; }
        .svc-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .svc-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .svc-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
        .svc-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 18px; }
        .svc-breadcrumb a { color: rgba(255,255,255,.75); text-decoration: none; }
        .svc-breadcrumb a:hover { color: var(--gold-l); }

        /* HIGHLIGHTS */
        .highlight-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(41,75,90,.08); border: 1px solid rgba(41,75,90,.08); border-radius: var(--radius); overflow: hidden; }
        @media (max-width: 900px) { .highlight-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 520px) { .highlight-grid { grid-template-columns: 1fr; } }
        .highlight-cell { background: #fff; padding: 28px 24px; }
        .highlight-cell .hl-icon { width: 42px; height: 42px; border-radius: 50%; background: var(--cream); color: var(--gold-d); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
        .highlight-cell h3 { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .highlight-cell p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0; }

        /* FEATURED SPLIT PANEL */
        .svc-split { display: grid; grid-template-columns: 1fr 1fr; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-md); border: 1px solid rgba(41,75,90,.09); background: #fff; }
        @media (max-width: 900px) { .svc-split { grid-template-columns: 1fr; } }
        .svc-split-media { position: relative; min-height: 320px; background-size: cover; background-position: center; }
        @media (min-width: 901px) { .svc-split-media { min-height: 100%; } }
        .svc-split-content { padding: 44px 42px; display: flex; flex-direction: column; justify-content: center; }
        .svc-split-title { font-size: clamp(1.4rem, 2vw, 1.9rem); font-weight: 600; color: var(--ink); line-height: 1.2; margin: 10px 0 14px; }
        .svc-split-text { font-size: 14.5px; color: var(--silver); font-weight: 300; line-height: 1.75; margin-bottom: 22px; }
        .split-list { list-style: none; padding: 0; margin: 0; }
        .split-list li { font-size: 13px; color: var(--teal); font-weight: 400; padding: 5px 0; display: flex; align-items: center; gap: 9px; }
        .split-list li::before { content: '✓'; color: var(--gold-d); font-weight: 700; }

        /* PROCESS */
        .rituals-rail { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; position: relative; }
        @media (max-width: 900px) { .rituals-rail { grid-template-columns: 1fr 1fr; gap: 28px; } }
        @media (max-width: 560px) { .rituals-rail { grid-template-columns: 1fr; } }
        .rituals-rail::before {
          content: '';
          position: absolute;
          top: 26px; left: 6%; right: 6%;
          height: 1px;
          background: repeating-linear-gradient(90deg, var(--gold-d) 0 8px, transparent 8px 16px);
          opacity: .5;
        }
        @media (max-width: 900px) { .rituals-rail::before { display: none; } }
        .ritual-card { position: relative; padding: 0 22px; text-align: left; }
        .ritual-card:first-child { padding-left: 0; }
        .ritual-num {
          position: relative; z-index: 1;
          width: 52px; height: 52px; border-radius: 50%;
          background: var(--teal-d); color: var(--gold-l);
          font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px; border: 1px solid var(--gold-d);
        }
        .ritual-card h3 { font-size: 1.02rem; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .ritual-card p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.7; margin: 0; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q {
          width: 100%; background: none; border: none; text-align: left; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          padding: 22px 4px; font-family: 'Playfair Display', serif; font-size: 1.02rem;
          font-weight: 500; color: var(--ink);
        }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 240px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 28px 20px; }
          .ritual-card { padding: 0; }
        }
`}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="svc-hero">
          <div className="svc-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="svc-hero-veil" />
          <div className="container svc-hero-inner">
            <div className="svc-breadcrumb"><a href="/services">Services</a> / Inbound Holidays</div>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Welcome to Oman
            </div>
            <h1 className="svc-hero-title">
              Inbound Holidays, <span className="gold">Built Around You</span>
            </h1>
            <p className="svc-hero-sub">
              Leisure trips, business travel, group tours, or full MICE logistics — arranged on the ground in Oman by people who know it well.
            </p>
            <div className="svc-hero-ctas">
              <a href="/contact" className="vbtn-primary">Get a Quote</a>
              <a href="/services" className="vbtn-ghost">All Services</a>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== HIGHLIGHTS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                What You Get
              </span>
              <h2 className="section-title centered">Built Around the Details That Matter</h2>
            </div>
            <div className="highlight-grid">
              {highlights.map((h) => (
                <div className="highlight-cell" key={h.title}>
                  <div className="hl-icon"><Icon name={h.icon} /></div>
                  <h3>{h.title}</h3>
                  <p>{h.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== SPLIT PANEL ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div className="svc-split">
              <div className="svc-split-media" style={{ backgroundImage: `url(${IMAGES.split})` }} />
              <div className="svc-split-content">
                <span className="eyebrow">The Details</span>
                <div className="svc-split-title">Oman, planned by people who live here</div>
                <p className="svc-split-text">Inbound travel works best when it's arranged locally — real knowledge of which roads close in the rain, which sites need advance booking, which restaurant can actually seat forty people. That's the difference between a itinerary on paper and one that runs smoothly.</p>
                <ul className="split-list">
              <li>Wadis, forts, and desert excursions</li>
              <li>Group and MICE logistics coordinated together</li>
              <li>Halal dining and prayer-time-aware scheduling</li>
              <li>English and Arabic-speaking guides</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== PROCESS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 52 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                How It Works
              </span>
              <h2 className="section-title centered">From First Message to Wheels Up</h2>
            </div>
            <div className="rituals-rail">
              {steps.map((s) => (
                <div className="ritual-card" key={s.n}>
                  <div className="ritual-num">{s.n}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Before You Book
              </span>
              <h2 className="section-title centered">Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.q}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FINAL CTA ===== */}
        <section className="section-pad bg-white" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Plan Your Time in Oman</div>
                <div className="cta-banner-sub">
                  Tell us who's coming, when, and what you're hoping to see — we'll build the itinerary around it.
                </div>
              </div>
              <a href="/contact" className="vbtn-primary">
                Get a Quote
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}